import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Button, Dialog, IconButton, Stack, TextField, Typography } from "@mui/material";
import { currenciesArray } from "constants/index";
import { Characteristic, IAssetClass } from "store/types";

import { OnboardingCharacteristicsSection } from "./OnboardingCharacteristicsSection";
import { InvestmentData } from "./Steps/InvestmentStep";

interface EditInvestmentModalProps {
  open: boolean;
  investment: InvestmentData | null;
  assetClassesData: IAssetClass[];
  characteristicsList: Characteristic[];
  categories: string[];
  onClose: () => void;
  onSave: (updatedInvestment: InvestmentData) => void;
}

export const EditInvestmentModal: React.FC<EditInvestmentModalProps> = ({
  open,
  investment,
  assetClassesData,
  characteristicsList,
  categories,
  onClose,
  onSave
}) => {
  const [editedInvestment, setEditedInvestment] = useState<InvestmentData | null>(investment);
  const [editedCharacteristics, setEditedCharacteristics] = useState<
    Array<{ characteristic_id: number; value: string; name?: string; currency?: string | null }>
  >(investment?.extractedData?.characteristics || []);

  // Update state when investment prop changes
  useEffect(() => {
    if (investment) {
      setEditedInvestment(investment);
      setEditedCharacteristics(investment.extractedData?.characteristics || []);
    }
  }, [investment, open]);

  const handleSave = () => {
    if (!editedInvestment) return;

    const updatedInvestment: InvestmentData = {
      dealName: editedInvestment.dealName,
      assetClass: editedInvestment.assetClass,
      subAssetClass: editedInvestment.subAssetClass,
      currency: editedInvestment.currency,
      geography: editedInvestment.geography,
      ...(editedInvestment.file && { file: editedInvestment.file }),
      extractedData: editedCharacteristics.length > 0 ? { characteristics: editedCharacteristics } : undefined
    };
    onSave(updatedInvestment);
    onClose();
  };

  const handleCharacteristicChange = (charId: number, value: string) => {
    setEditedCharacteristics((prev) => {
      const existingIndex = prev.findIndex((char) => char.characteristic_id === charId);

      if (existingIndex >= 0) {
        // Update existing characteristic
        return prev.map((char) => (char.characteristic_id === charId ? { ...char, value } : char));
      } else {
        // Add new characteristic
        const charDef = characteristicsList.find((c) => c.id === charId);
        return [...prev, { characteristic_id: charId, value, name: charDef?.name }];
      }
    });
  };

  const handleCurrencyChange = (charId: number, currency: string | null) => {
    setEditedCharacteristics((prev) => {
      const existingIndex = prev.findIndex((char) => char.characteristic_id === charId);

      if (existingIndex >= 0) {
        // Update existing characteristic
        return prev.map((char) => (char.characteristic_id === charId ? { ...char, currency } : char));
      } else {
        // Add new characteristic with currency
        const charDef = characteristicsList.find((c) => c.id === charId);
        return [...prev, { characteristic_id: charId, value: "", currency, name: charDef?.name }];
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "550px"
        }
      }}
    >
      <Box sx={{ p: "32px", position: "relative", borderRadius: "12px" }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "rgba(10, 49, 66, 0.6)"
          }}
        >
          <CloseIcon />
        </IconButton>

        {!editedInvestment ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ color: "rgba(10, 49, 66, 0.6)" }}>Loading...</Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#0a3142", mb: 1 }}>Edit Deal</Typography>
            <Typography sx={{ fontSize: "12px", color: "#5d7782", mb: 3 }}>
              Review and correct the extracted values before entering your workspace.
            </Typography>

            {/* Form Fields */}
            <Stack gap={2.5}>
              {/* Deal Name */}
              <Box>
                <TextField
                  fullWidth
                  label="Deal Name"
                  value={editedInvestment.dealName}
                  onChange={(e) => setEditedInvestment({ ...editedInvestment, dealName: e.target.value })}
                  placeholder="Enter Deal Name"
                />
              </Box>

              {/* Asset Class */}
              <Box>
                <Autocomplete
                  fullWidth
                  value={editedInvestment.assetClass}
                  onChange={(_, value) =>
                    setEditedInvestment({ ...editedInvestment, assetClass: value, subAssetClass: null })
                  }
                  options={assetClassesData}
                  getOptionLabel={(option) => option.account_alias || option.name}
                  renderInput={(params) => (
                    <TextField {...params} label="Asset Class" placeholder="Select Asset Class" />
                  )}
                />
              </Box>

              {/* Currency and Geography */}
              <Stack direction="row" gap={2}>
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    fullWidth
                    value={editedInvestment.currency}
                    onChange={(_, value) => setEditedInvestment({ ...editedInvestment, currency: value || "USD" })}
                    options={currenciesArray}
                    renderInput={(params) => <TextField {...params} label="Currency" placeholder="Select Currency" />}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Geography"
                    value={editedInvestment.geography}
                    onChange={(e) => setEditedInvestment({ ...editedInvestment, geography: e.target.value })}
                    placeholder="Enter Geography"
                  />
                </Box>
              </Stack>

              {/* Extracted Characteristics */}
              <OnboardingCharacteristicsSection
                characteristics={editedCharacteristics}
                characteristicsList={characteristicsList}
                categories={categories}
                onCharacteristicChange={handleCharacteristicChange}
                onCurrencyChange={handleCurrencyChange}
              />
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" gap={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button
                onClick={onClose}
                variant="text"
                sx={{
                  borderColor: "rgba(10, 49, 66, 0.2)",
                  color: "#0a3142",
                  fontSize: "15px",
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    borderColor: "rgba(10, 49, 66, 0.4)",
                    bgcolor: "rgba(10, 49, 66, 0.02)"
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  bgcolor: "#071f35",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#0f2d47"
                  }
                }}
              >
                Save Changes
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Dialog>
  );
};
